import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { propertyService, stayVocabularyService } from "@/lib/services";
import { extractErrorMessage } from "@/lib/api";
import { ImageUploader } from "@/components/image-uploader";
import { LocationPicker } from "@/components/location-picker";
import type { CreatePropertyPayload } from "@/types/api";

export const Route = createFileRoute("/_authenticated/host/properties/new")({
  component: NewPropertyPage,
});

function NewPropertyPage() {
  const navigate = useNavigate();
  const { data: vocabulary } = useQuery({
    queryKey: ["stay-vocabulary"],
    queryFn: ({ signal }) => stayVocabularyService.get(signal),
    staleTime: Infinity,
  });
  const propertyTypes = vocabulary?.property_types ?? [];
  const [form, setForm] = useState<CreatePropertyPayload>({
    name: "",
    description: "",
    address: "",
    lat: 0,
    lng: 0,
    property_type: "apartment",
    phone: "",
    cover_image: "",
  });
  // Image gallery (uploaded URLs). The first / starred one becomes cover_image.
  const [images, setImages] = useState<string[]>([]);
  const [cover, setCover] = useState<string | undefined>(undefined);

  const create = useMutation({
    mutationFn: (payload: CreatePropertyPayload) => propertyService.create(payload),
    onSuccess: (res) => {
      toast.success("Property created — awaiting verification.");
      navigate({ to: "/properties/$propertyId", params: { propertyId: res.data.id } });
    },
    onError: (err) => toast.error(extractErrorMessage(err, "Couldn't create property")),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.description || !form.address || !form.property_type) {
      toast.error("Please fill all required fields.");
      return;
    }
    if (form.lat < -90 || form.lat > 90 || form.lng < -180 || form.lng > 180) {
      toast.error("Latitude/longitude out of range.");
      return;
    }
    if (form.lat === 0 && form.lng === 0) {
      toast.error("Pick a location on the map.");
      return;
    }
    const resolvedCover = cover ?? images[0];
    const payload: CreatePropertyPayload = {
      ...form,
      cover_image: resolvedCover || undefined,
      phone: form.phone?.trim() || undefined,
    };
    create.mutate(payload);
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <h1 className="text-3xl font-extrabold tracking-tight text-ink">Add a property</h1>
      <p className="mt-1 text-sm text-ink-muted">
        After submission an admin will verify your property before it goes live.
      </p>

      <form onSubmit={submit} className="mt-6 space-y-5">
        <Field label="Name" required>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="input"
            maxLength={255}
            required
          />
        </Field>
        <Field label="Description" required>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="input min-h-[120px] py-3"
            required
          />
        </Field>
        <Field label="Address" required>
          <input
            type="text"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            className="input"
            maxLength={255}
            required
          />
        </Field>

        <div>
          <span className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-ink-muted">
            Location <span className="text-destructive">*</span>
          </span>
          <LocationPicker
            lat={form.lat}
            lng={form.lng}
            onChange={(lat, lng) => setForm({ ...form, lat, lng })}
          />
          <div className="mt-2 grid grid-cols-2 gap-3">
            <input
              type="number"
              step="any"
              value={form.lat || ""}
              onChange={(e) => setForm({ ...form, lat: Number(e.target.value) })}
              className="input"
              placeholder="Latitude"
            />
            <input
              type="number"
              step="any"
              value={form.lng || ""}
              onChange={(e) => setForm({ ...form, lng: Number(e.target.value) })}
              className="input"
              placeholder="Longitude"
            />
          </div>
        </div>

        <Field label="Property type" required>
          <select
            value={form.property_type}
            onChange={(e) =>
              setForm({
                ...form,
                property_type: e.target.value as CreatePropertyPayload["property_type"],
              })
            }
            className="input"
          >
            {propertyTypes.map((option) => (
              <option key={option.value} value={option.value}>
                {option.icon} {option.label}
              </option>
            ))}
          </select>
        </Field>

        <ImageUploader
          label="Property photos"
          value={images}
          onChange={setImages}
          cover={cover ?? images[0]}
          onCoverChange={setCover}
          max={10}
        />

        <Field label="Phone">
          <input
            type="tel"
            value={form.phone ?? ""}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="input"
            maxLength={20}
          />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => navigate({ to: "/host/properties" })}
            className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-bold text-ink hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90 disabled:opacity-60"
          >
            {create.isPending ? "Creating…" : "Create property"}
          </button>
        </div>
      </form>

      <style>{`.input{display:block;width:100%;height:42px;border-radius:10px;border:1px solid var(--input);background:var(--background);padding:0 12px;font-size:14px;color:var(--ink);outline:none;} .input:focus{border-color:var(--primary);} textarea.input{height:auto;}`}</style>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ink-muted">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </span>
      {children}
    </label>
  );
}
